import {
  insuranceProductStatuses,
  insuranceAffiliateStatuses,
  insuranceCoverageStatuses,
} from "@luntero/db/contracts/insurance";
import type { InsuranceType } from "@luntero/db/contracts/insurance";
export {
  type InsuranceType,
  insuranceTypes,
  type InsuranceProductKind,
  insuranceProductKinds,
  type InsuranceAvailability,
  insuranceAvailabilityOptions,
  type InsuranceRelatedProduct,
  insuranceRelatedProductSchema,
  type InsuranceFact,
  insuranceFactSchema,
  insuranceProductStatuses,
  insuranceAffiliateStatuses,
  type InsuranceChannel,
  insuranceChannels,
  type InsurancePricing,
  insurancePricingSchema,
  type InsuranceEligibility,
  insuranceEligibilitySchema,
  insuranceEligibilityStatuses,
  type InsuranceCoverageItem,
  insuranceCoverageItemSchema,
  insuranceCoverageStatuses,
  type InsuranceContract,
  insuranceContractSchema,
  type InsuranceSource,
  insuranceSourceSchema,
  type InsuranceFactNotes,
  insuranceFactNotesSchema,
  type InsuranceCoverageText,
  insuranceCoverageTextSchema,
} from "@luntero/db/contracts/insurance";

// Consumer products offered in the Netherlands, independent of affiliate access.

export type InsuranceProductStatus = (typeof insuranceProductStatuses)[number];

export type InsuranceAffiliateStatus =
  (typeof insuranceAffiliateStatuses)[number];

export type InsuranceCoverageStatus =
  (typeof insuranceCoverageStatuses)[number];

// JSON dates are ISO strings. Unknown facts are omitted/null, never false or zero.
// Keys connect factual data to localized factNotes/coverageTexts and source IDs.

// Family keys group separately described tiers/policies. Links describe real
// product dependencies or bundle contents, not affiliate or provider ownership.

// Category-specific facts (territory, pet species, policy model, insured age,
// etc.). String values are stable codes; localized explanations use factNotes.
// Numeric units use stable keys, e.g. days, percent, eur, sessions, kilometers.

// These schemas define JSON column shapes. Drizzle .$type does not perform runtime
// validation. The uploader parses these shapes; manual data preparation checks
// cross-references, unique keys, localized explanations and source accuracy.

// Presentation grouping for the represented insurance types. Every type sits
// in exactly one family; the order is the order families and types render in.
export const insuranceTypeFamilies = {
  HOME: [
    "PERSONAL_LIABILITY",
    "CONTENTS",
    "BUILDINGS",
    "VALUABLES",
    "GLASS",
    "HOME_CONSTRUCTION",
    "RECREATIONAL_HOME",
    "HOUSEBOAT",
  ],
  MOBILITY: [
    "CAR",
    "MOTORCYCLE",
    "MOPED",
    "BICYCLE",
    "MOBILITY_SCOOTER",
    "MICRO_MOBILITY",
    "CLASSIC_VEHICLE",
    "CARAVAN",
    "CAMPER",
    "TRAILER",
    "BOAT",
    "BREAKDOWN_ASSISTANCE",
    "VEHICLE_OCCUPANTS",
    "RENTAL_VEHICLE_EXCESS",
  ],
  TRAVEL: ["TRAVEL", "TRAVEL_CANCELLATION"],
  HEALTH: [
    "HEALTH_BASIC",
    "HEALTH_SUPPLEMENTARY",
    "DENTAL",
    "INTERNATIONAL_HEALTH",
    "ACCIDENT",
  ],
  LEGAL_INCOME: ["LEGAL_EXPENSES", "DISABILITY", "PAYMENT_PROTECTION", "CYBER"],
  LIFE: ["TERM_LIFE", "LIFE", "FUNERAL"],
  ANIMALS: ["PET", "HORSE"],
  OTHER: ["ELECTRONICS", "WEDDING_EVENT", "PACKAGE", "OTHER_PERSONAL"],
} as const satisfies Record<string, readonly InsuranceType[]>;

export type InsuranceTypeFamily = keyof typeof insuranceTypeFamilies;

export const insuranceTypeFamilyOrder = Object.keys(
  insuranceTypeFamilies,
) as InsuranceTypeFamily[];

const familyByType = new Map<InsuranceType, InsuranceTypeFamily>(
  insuranceTypeFamilyOrder.flatMap((family) =>
    insuranceTypeFamilies[family].map(
      (type) => [type, family] as [InsuranceType, InsuranceTypeFamily],
    ),
  ),
);

export const getInsuranceTypeFamily = (type: InsuranceType) =>
  familyByType.get(type) ?? "OTHER";

/** Render order of a type: its family first, then its place inside it. */
export const insuranceTypeOrder = new Map<InsuranceType, number>(
  insuranceTypeFamilyOrder
    .flatMap((family) => insuranceTypeFamilies[family])
    .map((type, index) => [type, index]),
);

/** The covers that come up first when someone rents a home in the Netherlands. */
export const rentingEssentialInsuranceTypes = [
  "PERSONAL_LIABILITY",
  "CONTENTS",
  "LEGAL_EXPENSES",
  "HEALTH_BASIC",
] as const satisfies readonly InsuranceType[];
